import { API_BASE_URL } from '../api/config';
import type { BaseEntityType } from '../types';
import type { EntityResponse } from '../types/entities';
import type { NoteEntity } from '../types/notes';
import fetchService from './fetchService';
import { createFileLogger } from 'utils/logger';
import useUsageLimitsStore, { type UsageLimitInfo } from '../stores/usageLimitsStore';
import { useDataspaceContextStore } from '../stores/dataspaceContextStore';
import { generateUniqueLLMId } from '../utils/entityIdGenerator';
import { requestCacheService } from './requestCacheService';
import { extractLimitExceededError } from '../utils/planUtils';

const log = createFileLogger('ENTITY_SERVICE', {enabled: false});

interface EntityServiceConfig {
  baseUrl: string;
}

interface EntityQueryParams {
  type?: string; // Maps to entityType in MongoDB
  userId?: string;
  entityType?: string; // Direct MongoDB field mapping
  name?: string;
  appId?: string;
  created_at?: string;
  updated_at?: string;
  ids?: string;  // Comma-separated list of entity IDs/llmIds
  [key: string]: any; // Allow for additional MongoDB query parameters
}

/**
 * A single result from GET /api/entities/search.
 * entity_id is the MongoDB _id of the entity.
 * score is the MongoDB textScore relevance value.
 * snippet is a short excerpt from the extracted search_text near the matched term.
 */
export interface EntitySearchResult {
  entity_id: string;
  name: string;
  entityType: string;
  llmId?: string;
  icon?: string;
  score?: number;
  snippet?: string;
}

interface FreemiumLimitError {
  error: string;
  message: string;
  limit_type?: string;
  plan_type?: string;
  limits: {
    monthly_cost?: { used: number; max: number; exceeded?: boolean };
    imageGenerations?: { used: number; max: number; exceeded?: boolean };
    toolCalls?: { used: number; max: number; exceeded?: boolean };
    acceptedEdits?: { used: number; max: number; exceeded?: boolean };
    userMessages?: { used: number; max: number; exceeded?: boolean };
    [key: string]: { used: number; max: number; exceeded?: boolean } | undefined;
  };
  upgrade_required: boolean;
}

export class EntityService<T extends BaseEntityType> {
  private baseUrl: string;

  constructor(config?: EntityServiceConfig) {
    // SMART ENVIRONMENT DETECTION: Works for both local and production
    const hostname = window.location.hostname;
    
    // Always use hostname detection - ignore .env file in production
    const smartApiUrl = hostname === 'app.riversystem.ai' || hostname === 'riversystem.ai' || hostname.endsWith('.riversystem.ai')
      ? 'https://app.riversystem.ai/api'   // Selfhosted
      : hostname === 'rivereditor.com' 
      ? 'https://api.rivereditor.com/api'  // Production - Direct to ALB (bypasses CloudFront 60s limit)
      : 'http://localhost:5001/api';       // Local development
    
    // Only use config.baseUrl if explicitly provided, otherwise use smart detection
    this.baseUrl = config?.baseUrl || smartApiUrl;
  }

  private handleFreemiumError(error: any, suppressModal: boolean = false): void {
    const limitError = extractLimitExceededError(error);
    if (limitError) {
      const planName: string = limitError.plan_type || 'free';
      const limitType = (limitError.limit_type || 'monthly_cost') as UsageLimitInfo['limitType'];

      if (!suppressModal) {
        log.info('[entityService] Limit exceeded, showing upgrade modal');
        useUsageLimitsStore.getState().showUpgradeModal({
          limitType,
          currentUsage: {
            used: limitError.limits?.[limitType]?.used || 0,
            max: limitError.limits?.[limitType]?.max || 0,
            unit: 'dollars',
          },
          planName,
          message: limitError.message,
        });
      } else {
        log.info('[entityService] Limit exceeded, modal suppressed');
      }

      throw new Error('Entity creation blocked due to plan limits');
    }
  }

  async create(data: Partial<T>, options?: { suppressFreemiumModal?: boolean }): Promise<T> {
    try {
      // Generate unique LLMId if not provided
      const entityData = data as any; // Cast to handle llmId property
      if (!entityData.llmId && entityData.entityType) {
        const llmId = await generateUniqueLLMId(entityData.entityType, async (id: string) => {
          try {
            // Use HEAD request to check existence without fetching full entity
            const response = await fetchService(`${this.baseUrl}/entities/${id}`, {
              method: 'HEAD',
              credentials: 'include',
            });
            return true; // Entity exists (HEAD returned 200)
          } catch (error: any) {
            if (error.status === 404) {
              return false; // Entity doesn't exist
            }
            // For other errors, assume it doesn't exist to be safe
            return false;
          }
        });
        
        entityData.llmId = llmId;
        log.info('[EntityService] Generated unique LLMId:', entityData.llmId);
        
        // ✅ FIX: Pre-register mapping before backend creation to prevent 404s
        const { llmIdMapper } = await import('../utils/llmIdMapper');
        // Create a temporary mapping that will be updated with actual ID after creation
        llmIdMapper.preRegisterLlmId(llmId, entityData.entityType, entityData.name || 'untitled');
      }
      
      // ✅ ORGS V1: Auto-set org_id based on current dataspace context
      // Check for both undefined and null to handle all cases
      if (!entityData.org_id || entityData.org_id === null) {
        const { getCurrentOrgId, getCurrentDataspaceContext } = await import('../stores/dataspaceContextStore');
        const currentOrgId = getCurrentOrgId();
        const context = getCurrentDataspaceContext();
        
        log.info('[EntityService] 🌐 Current dataspace context:', context);
        log.info('[EntityService] 🌐 Current org ID:', currentOrgId);
        
        if (currentOrgId) {
          entityData.org_id = currentOrgId;
          log.info('[EntityService] ✅ Auto-set org_id from dataspace context:', currentOrgId);
          
          // ✅ ORGS V1: Auto-add org ACL for entities created in org context
          // Always add org ACL (append to existing ACL if present)
          const orgAclEntry = {
            subject: `org:${currentOrgId}`,
            role: 'editor',
            granted_at: new Date().toISOString(),
            granted_by: 'system'
          };
          
          if (!entityData.acl) {
            entityData.acl = [orgAclEntry];
          } else {
            // Check if org ACL already exists
            const hasOrgAcl = entityData.acl.some((entry: any) => entry.subject === `org:${currentOrgId}`);
            if (!hasOrgAcl) {
              entityData.acl.push(orgAclEntry);
            }
          }
          log.info('[EntityService] ✅ Auto-added org ACL for entity in org context');
        } else {
          log.info('[EntityService] ℹ️ No org context - entity will be in Personal dataspace');
        }
      } else {
        log.info('[EntityService] 📋 org_id already set:', entityData.org_id);
      }
      
      log.info('[EntityService] 📤 Creating entity with org_id:', entityData.org_id);

      // Don't set _id - let the backend generate it
      log.info('[EntityService] Creating entity with data:', entityData);

      const response = await fetchService(`${this.baseUrl}/entities`, {
        method: 'POST',
        body: JSON.stringify(entityData),
        credentials: 'include',
      });

      log.info('[EntityService] Created entity response:', response);
      
      // ✅ CRITICAL FIX: Ensure llmId and name are preserved in the response
      const responseData = response as any;
      if (entityData.llmId && !responseData.llmId) {
        log.warn('[EntityService] Backend did not return llmId, preserving from request data');
        responseData.llmId = entityData.llmId;
      }
      
      // ✅ CRITICAL FIX: Ensure name is preserved in the response
      if (entityData.name && !responseData.name) {
        log.warn('[EntityService] Backend did not return name, preserving from request data');
        responseData.name = entityData.name;
      }

      // ✅ FIX: Update pre-registered mapping with actual entity ID
      if (entityData.llmId && (responseData._id || responseData.id)) {
        const { llmIdMapper } = await import('../utils/llmIdMapper');
        const actualId = responseData._id || responseData.id;
        llmIdMapper.registerMapping(
          entityData.llmId, 
          actualId, 
          'document', 
          entityData.entityType, 
          entityData.name
        );
        log.info('[EntityService] Updated llmId mapping with actual ID:', { 
          llmId: entityData.llmId, 
          actualId 
        });
      }

      // ✅ CRITICAL FIX: Register mapping synchronously to ensure immediate availability
      if (responseData.llmId && responseData._id) {
        try {
          const { llmIdMapper } = await import('../utils/llmIdMapper');
          llmIdMapper.registerMapping(responseData.llmId, responseData._id, 'document', entityData.entityType, entityData.name);
          log.info('[EntityService] ✅ Registered llmId mapping:', { 
            llmId: responseData.llmId, 
            actualId: responseData._id,
            entityType: entityData.entityType 
          });
        } catch (mappingError) {
          log.warn('[EntityService] Failed to register llmId mapping:', mappingError);
        }
      }

      log.info('[EntityService] Final entity with llmId:', { 
        id: responseData._id, 
        llmId: responseData.llmId,
        hasLlmId: !!responseData.llmId 
      });

      // ✅ PERFORMANCE: Removed redundant validation GET request (saves 100-200ms)
      // MongoDB is ACID-compliant - if insert succeeds, entity exists
      // Backend already returns full entity, so no need to re-fetch
      
      // 🚀 EMIT ENTITY EVENT: Notify EntityStore about new entity
      try {
        const { entityEventService } = await import('./entityEventService');
        
        // ✅ CRITICAL FIX: Add entity to entityStore IMMEDIATELY after creation
        // This ensures it appears in hierarchyStore, file tree, and recent spaces
        try {
          const { default: useEntityStore } = await import('../stores/entityStore');
          const entityStore = useEntityStore.getState();
          entityStore.addEntity(responseData as BaseEntityType);
          log.info('[EntityService] ✅ Added entity to entityStore:', {
            entityId: responseData._id,
            entityType: responseData.entityType,
            name: responseData.name
          });
        } catch (storeError) {
          log.error('[EntityService] ❌ Failed to add entity to entityStore:', storeError);
        }
        
        // ✅ FIX: For Space entities, they don't have spaceId (they ARE the space)
        // For other entities, get spaceId from entity data
        const spaceId = entityData.spaceId || responseData.spaceId || 
                        (responseData.entityType === 'Space' ? responseData._id : null);
        
        if (!spaceId) {
          log.warn('[EntityService] ⚠️ Cannot emit entityCreated event - no spaceId available:', {
            entityId: responseData._id,
            entityType: responseData.entityType,
            llmId: responseData.llmId,
            hasEntityDataSpaceId: !!entityData.spaceId,
            hasResponseDataSpaceId: !!responseData.spaceId
          });
        } else {
          entityEventService.entityCreated(
            responseData as BaseEntityType,
            spaceId,
            {
              command: 'entity_create',
              userId: entityData.userId
            }
          );
          log.info('[EntityService] ✅ Emitted entityCreated event:', {
            entityId: responseData._id,
            entityType: responseData.entityType,
            llmId: responseData.llmId,
            name: responseData.name,
            spaceId: spaceId
          });
        }
      } catch (eventError) {
        log.warn('[EntityService] Failed to emit entity creation event (non-critical):', eventError);
        // Don't throw - entity was created successfully
      }
      
      return responseData;
    } catch (error: any) {
      this.handleFreemiumError(error, options?.suppressFreemiumModal);
      throw error;
    }
  }

  async get(id: string, options?: { excludeContent?: boolean }): Promise<T> {
    // Try to resolve LLMId to actual ID first
    const { resolveToActualId, registerEntityMapping } = await import('../utils/llmIdMapper');
    const actualId = resolveToActualId(id);
    
    // ✅ PERFORMANCE FIX: Use request cache to deduplicate simultaneous entity fetches
    // Multiple components (EntityViewApp, spaceCommands, spaceStore, etc.) often fetch the same entity
    const cacheKey = `entity-${actualId}-${options?.excludeContent ? 'metadata' : 'full'}`;
    
    return requestCacheService.getOrFetch(cacheKey, async () => {
      const authToken = localStorage.getItem('authToken');
      
      // Build query parameters
      const params = new URLSearchParams();
      if (options?.excludeContent) {
        params.append('excludeContent', 'true');
      }
      const queryString = params.toString();
      const urlSuffix = queryString ? `?${queryString}` : '';
      
      // For unauthenticated users, try public endpoint first
      if (!authToken) {
        try {
          const entity = await fetchService(`${this.baseUrl}/public-entities/${actualId}${urlSuffix}`, {
            credentials: 'include',
          });

          // Register the mapping for future use
          registerEntityMapping(entity);

          log.info(`[EntityService] ✅ Fetched entity ${actualId} from public endpoint`, {
            excludeContent: options?.excludeContent,
            hasBase64: !!(entity.skeleton?.base64)
          });
          return entity;
        } catch (publicError) {
          // If public access fails, throw appropriate error
          const { DocumentAccessError } = await import('../types/spaces');
          throw new DocumentAccessError('Permission denied', actualId);
        }
      }
      
      // For authenticated users, use regular endpoint
      try {
        const startTime = performance.now();
        const entity = await fetchService(`${this.baseUrl}/entities/${actualId}${urlSuffix}`, {
          credentials: 'include',
        });
        const fetchTime = performance.now() - startTime;

        // Register the mapping for future use
        registerEntityMapping(entity);

        // Calculate payload size for monitoring
        const payloadSize = JSON.stringify(entity).length;
        const payloadSizeKB = (payloadSize / 1024).toFixed(2);

        log.info(`[EntityService] ✅ Fetched entity ${actualId} from backend`, {
          excludeContent: options?.excludeContent,
          hasBase64: !!(entity.skeleton?.base64),
          payloadSizeKB: `${payloadSizeKB} KB`,
          fetchTimeMs: `${fetchTime.toFixed(0)}ms`
        });
        return entity;
      } catch (error) {
        // Handle access denied errors specifically for documents
        if ((error as any)?.status === 403 || 
            (error instanceof Error && error.message.includes('403'))) {
          const { DocumentAccessError } = await import('../types/spaces');
          // Try to extract document name from error data if available
          const errorData = (error as any)?.data;
          const documentName = errorData?.name || errorData?.title;
          throw new DocumentAccessError('Permission denied', actualId, documentName);
        }
        
        // Re-throw other errors as-is
        throw error;
      }
    });
  }

  async update(id: string, data: Partial<T>): Promise<T> {
    // Try to resolve LLMId to actual ID first
    const { resolveToActualId, registerEntityMapping } = await import('../utils/llmIdMapper');
    const actualId = resolveToActualId(id);
    
    const entity = await fetchService(`${this.baseUrl}/entities/${actualId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      credentials: 'include',
    });

    // Register/update the mapping for future use
    registerEntityMapping(entity);

    return entity;
  }

  async mergeUpdate(id: string, data: Partial<T>): Promise<T> {
    // Try to resolve LLMId to actual ID first
    const { resolveToActualId, registerEntityMapping } = await import('../utils/llmIdMapper');
    const actualId = resolveToActualId(id);
    
    const entity = await fetchService(`${this.baseUrl}/entities/${actualId}/merge`, {
      method: 'PUT',
      body: JSON.stringify(data),
      credentials: 'include',
    });

    // Register/update the mapping for future use
    registerEntityMapping(entity);

    // ✅ ARCHITECTURE: Event emission removed - let store actions handle notifications
    // The store subscription system will propagate changes to all consumers

    return entity;
  }

  async fetchEntity(id: string): Promise<EntityResponse> {
    // Try to resolve LLMId to actual ID first
    const { resolveToActualId, registerEntityMapping } = await import('../utils/llmIdMapper');
    const actualId = resolveToActualId(id);
    
    const entity = await fetchService(`${this.baseUrl}/entities/${actualId}`);

    // Register the mapping for future use
    registerEntityMapping(entity);

    return entity;
  }

  /**
   * Fetch only the version number for a document.
   * 
   * 🚀 PERFORMANCE: This endpoint saves ~500KB-2MB I/O per request by not fetching
   * the full document (especially skeleton.base64). Use this for cache validation
   * where you only need to check if your cached version is stale.
   */
  async getVersion(id: string): Promise<{ version: number; _id: string }> {
    const { resolveToActualId } = await import('../utils/llmIdMapper');
    const actualId = resolveToActualId(id);
    
    return fetchService(`${this.baseUrl}/entities/${actualId}/version`);
  }

  /**
   * Get all entity timestamps for staleness detection.
   * Much more efficient than fetching full entities (5KB vs 200KB).
   * 
   * This endpoint only returns llmId + updated_at for each entity,
   * enabling efficient cache validation without the overhead of
   * fetching full entity data.
   * 
   * Used by EntityStore for background staleness checks.
   * 
   * Returns: { "doc_abc": "2026-01-06T12:34:56Z", "note_xyz": "2026-01-05T10:20:30Z", ... }
   */
  async getEntityTimestamps(): Promise<Record<string, string>> {
    try {
      const timestamps = await fetchService(`${this.baseUrl}/entities/timestamps`);
      
      log.info('[EntityService] ✅ Fetched entity timestamps', {
        count: Object.keys(timestamps).length,
        sampleKeys: Object.keys(timestamps).slice(0, 5),
        estimatedSizeKB: (JSON.stringify(timestamps).length / 1024).toFixed(2)
      });
      
      return timestamps;
    } catch (error) {
      log.error('[EntityService] Failed to fetch entity timestamps:', error);
      throw error;
    }
  }

  async fetchNotes(): Promise<NoteEntity[]> {
    return fetchService(`${this.baseUrl}/entities?type=AppleNote`);
  }

  async fetchEntities(): Promise<T[]> {
    const entities = await fetchService(`${this.baseUrl}/entities`, {
      credentials: 'include',
    });

    // Register mappings for all entities that have llmId
    const { registerEntityMapping } = await import('../utils/llmIdMapper');
    entities.forEach(registerEntityMapping);

    return entities;
  }

  async queryEntities(query: EntityQueryParams): Promise<T[]> {
    log.info(`[EntityService] Querying entities with params:`, query);

    try {
      // CRITICAL FIX: Check if IDs list is too long for URL and batch if needed
      const MAX_URL_LENGTH = 6000; // Safe limit to avoid CloudFront 494 errors
      const BATCH_SIZE = 200; // Max IDs per batch
      
      if (query.ids && query.ids.length > 0) {
        const idsArray = query.ids.split(',');
        
        // Calculate accurate URL length including encoding overhead
        const baseQueryParams = Object.entries(query)
          .filter(([key]) => key !== 'ids')
          .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
          .join('&');
        const baseUrlOverhead = this.baseUrl.length + 20; // '/entities?' + safety margin
        const encodedIdsLength = idsArray
          .map(id => encodeURIComponent(id))
          .join(',').length;
        const estimatedUrlLength = baseUrlOverhead + baseQueryParams.length + 5 + encodedIdsLength; // 5 for 'ids='
        
        // If URL would be too long, batch the requests
        if (estimatedUrlLength > MAX_URL_LENGTH || idsArray.length >= BATCH_SIZE) {
          log.warn(`[EntityService] ⚠️ Large ID list detected (${idsArray.length} ids, ~${estimatedUrlLength} chars). Batching requests...`);
          
          const batches: string[][] = [];
          for (let i = 0; i < idsArray.length; i += BATCH_SIZE) {
            batches.push(idsArray.slice(i, i + BATCH_SIZE));
          }
          
          log.info(`[EntityService] 📦 Split into ${batches.length} batches of ${BATCH_SIZE} IDs each`);
          
          // Execute batches SEQUENTIALLY with error resilience (continues on failures)
          const allResults: T[] = [];
          let successCount = 0;
          let failureCount = 0;
          
          for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];
            const batchQuery = { ...query, ids: batch.join(',') };
            
            log.info(`[EntityService] 🔄 Fetching batch ${i + 1}/${batches.length} (${batch.length} IDs)`);
            
            try {
              const batchResult = await this._querySingleBatch(batchQuery);
              allResults.push(...batchResult);
              successCount++;
            } catch (error) {
              failureCount++;
              log.error(`[EntityService] ❌ Batch ${i + 1}/${batches.length} failed:`, error);
              // Continue with remaining batches despite failure
            }
          }
          
          log.info(`[EntityService] ✅ Batched query complete: ${allResults.length} entities fetched (${successCount} successful batches, ${failureCount} failed)`);
          
          if (failureCount > 0 && allResults.length === 0) {
            throw new Error(`All ${batches.length} batches failed`);
          }
          
          return allResults;
        }
      }
      
      // Normal single request path
      return await this._querySingleBatch(query);
    } catch (error) {
      log.error(`[EntityService] Failed to query entities:`, error);
      throw error;
    }
  }

  /**
   * INTERNAL: Execute a single query batch (extracted to avoid duplication)
   */
  private async _querySingleBatch(query: EntityQueryParams): Promise<T[]> {
    // Transform the query to match MongoDB structure
    const mongoQuery: Record<string, any> = {};

    // Handle type/entityType mapping
    if (query.type) {
      mongoQuery.entityType = query.type;
    } else if (query.entityType) {
      mongoQuery.entityType = query.entityType;
    }

    // Process all other query parameters
    Object.entries(query).forEach(([key, value]) => {
      // Skip already processed parameters
      if (key === 'type' || key === 'entityType') return;

      // Handle date fields
      if (key === 'created_at' || key === 'updated_at') {
        if (value instanceof Date) {
          mongoQuery[key] = value.toISOString();
        } else if (typeof value === 'string') {
          mongoQuery[key] = value;
        }
      }
      // Handle array fields
      else if (Array.isArray(value)) {
        mongoQuery[key] = value.join(',');
      }
      // Handle boolean fields
      else if (typeof value === 'boolean') {
        mongoQuery[key] = value.toString();
      }
      // Handle numeric fields
      else if (typeof value === 'number') {
        mongoQuery[key] = value.toString();
      }
      // Default: string value or other types
      else {
        mongoQuery[key] = value;
      }
    });

    // Build query string
    const queryString = Object.entries(mongoQuery)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');

    const response = await fetchService(`${this.baseUrl}/entities?${queryString}`, {
      credentials: 'include',
    });

    if (!Array.isArray(response)) {
      log.error('[EntityService] Expected array response, got:', response);
      throw new Error('Invalid response format from /api/entities');
    }

    // Register mappings for all entities that have llmId
    const { registerEntityMapping } = await import('../utils/llmIdMapper');
    response.forEach(registerEntityMapping);

    return response;
  }

  async searchEntities(
    searchQuery: string,
    entityTypes?: string[],
    limit?: number,
    semantic?: boolean,
    signal?: AbortSignal,
  ): Promise<EntitySearchResult[]> {
    log.info(`[EntityService] Searching entities: "${searchQuery}" semantic=${semantic ?? false}`);

    try {
      const params = new URLSearchParams({ q: searchQuery });

      if (entityTypes && entityTypes.length > 0) {
        params.set('entity_types', entityTypes.join(','));
      }
      if (limit) {
        params.set('limit', limit.toString());
      }
      if (semantic) {
        params.set('semantic', 'true');
      }

      const response = await fetchService(`${this.baseUrl}/entities/search?${params.toString()}`, {
        credentials: 'include',
        signal,
      });

      if (!Array.isArray(response)) {
        log.error(`[EntityService] Expected array response from search, got:`, typeof response);
        return [];
      }

      log.info(`[EntityService] Search found ${response.length} results for "${searchQuery}"`);

      // Register llmId mappings for results that carry one
      const { registerEntityMapping } = await import('../utils/llmIdMapper');
      response.forEach((r: any) => {
        if (r.llmId) registerEntityMapping({ _id: r.entity_id, llmId: r.llmId } as any);
      });

      return response as EntitySearchResult[];
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        throw error;
      }
      log.error(`[EntityService] Failed to search entities:`, error);
      throw error;
    }
  }

  async batchUpsert(entities: Partial<T>[]): Promise<T[]> {
    try {
      return await fetchService(`${this.baseUrl}/entities/batch`, {
        method: 'POST',
        body: JSON.stringify({ entities }),
        credentials: 'include',
      });
    } catch (error: any) {
      this.handleFreemiumError(error);
      throw error;
    }
  }

  async deleteEntity(id: string, permanent: boolean = false): Promise<boolean | T> {
    try {
      // Try to resolve LLMId to actual ID first
      const { resolveToActualId, llmIdMapper } = await import('../utils/llmIdMapper');
      const actualId = resolveToActualId(id);
      
      if (permanent) {
        // Hard delete - completely remove from database
        log.info(`[EntityService] Permanently deleting entity ${actualId}...`);
        
        // Get entity from store before deleting (for event emission)
        const useEntityStore = (await import('../stores/entityStore')).default;
        const entity = useEntityStore.getState().entities.find(e => e._id === actualId);
        
        try {
          await fetchService(`${this.baseUrl}/entities/${actualId}`, {
            method: 'DELETE',
            credentials: 'include',
            mode: 'cors',
          });

          // Remove mapping if it was an LLMId
          if (llmIdMapper.hasLLMId(id)) {
            llmIdMapper.removeMapping(id);
          }

          log.info(`[EntityService] Successfully permanently deleted entity ${actualId}`);
          
          // Trigger space window cleanup in real-time
          this._triggerSpaceWindowCleanup(actualId, id);
          
          // Directly remove from store to ensure IndexedDB gets updated
          if (entity) {
            useEntityStore.getState()._applyLocalDelete(actualId, {
              source: 'local',
              log: false,
              syncCRDT: false  // Already deleted from backend
            });
            log.info(`[EntityService] Removed entity ${actualId} from store cache`);
          }
        } catch (deleteError: any) {
          // If entity is already deleted (404), that's okay - consider it a success
          if (deleteError?.message?.includes('404') || deleteError?.status === 404) {
            log.info(`[EntityService] Entity ${actualId} already deleted (404), treating as success`);
            
            // Remove mapping if it was an LLMId
            if (llmIdMapper.hasLLMId(id)) {
              llmIdMapper.removeMapping(id);
            }
            
            // Trigger cleanup anyway
            this._triggerSpaceWindowCleanup(actualId, id);
            
            // Directly remove from store even for 404 to clean up frontend cache
            if (entity) {
              useEntityStore.getState()._applyLocalDelete(actualId, {
                source: 'local',
                log: false,
                syncCRDT: false  // Already deleted/missing from backend
              });
              log.info(`[EntityService] Removed already-deleted entity ${actualId} from store cache`);
            }
            
            return true;
          }
          throw deleteError;
        }
      } else {
        // Soft delete - mark as trashed
        log.info(`[EntityService] Soft deleting entity ${actualId} (marking as trashed)...`);
        
        const updatedEntity = await this.update(actualId, { trashed: true } as any);
        
        log.info(`[EntityService] Successfully soft deleted entity ${actualId}`);
        
        // Return the updated entity so caller can use confirmed backend data
        return updatedEntity;
      }
      
      return true;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        log.debug('Entity already deleted or not found');
        return false;
      }
      log.error('Delete request failed:', error);
      return false;
    }
  }

  async batchDeleteEntities(ids: string[], permanent: boolean = false): Promise<{ deleted: string[]; failed: string[] }> {
    log.info(`[EntityService] Batch deleting ${ids.length} entities (permanent: ${permanent})`);

    const deleted: string[] = [];
    const failed: string[] = [];

    // Use Promise.all to delete entities in parallel for better performance
    const deletePromises = ids.map(async (id) => {
      try {
        const success = await this.deleteEntity(id, permanent);
        if (success) {
          deleted.push(id);
        } else {
          failed.push(id);
        }
      } catch (error) {
        log.error(`[EntityService] Failed to delete entity ${id}:`, error);
        failed.push(id);
      }
    });

    await Promise.all(deletePromises);

    log.info(`[EntityService] Batch delete completed: ${deleted.length} deleted, ${failed.length} failed`);
    return { deleted, failed };
  }

  async getEntities(spaceId: string): Promise<BaseEntityType[]> {
    log.info(`[ENTITY_API_CALL] getEntities() | SpaceId: ${spaceId}`);

    try {
      // Get all entities the user has access to (owned + shared via ACL)
      const response = await fetchService(`${API_BASE_URL}/entities`);
      
      if (!Array.isArray(response)) {
        log.error('[EntityService] Expected array response, got:', response);
        throw new Error('Invalid response format from /api/entities');
      }

      // Filter entities: include entities in this space, plus dataspace-level entities
      // (those with no spaceId — e.g. Integration.*, Mail.Thread, Contact).
      // Entities explicitly belonging to a different space are excluded.
      const relevantEntities = response.filter((entity: any) => {
        // Entities that explicitly belong to this space
        if (entity.spaceId === spaceId) return true;
        // Org/dataspace-level entities have no spaceId — they belong to the dataspace,
        // not to any specific space, and should always be accessible.
        if (!entity.spaceId) return true;
        // Exclude entities that belong to a different space
        return false;
      });

      log.info(`[EntityService] getEntities filtered: ${relevantEntities.length}/${response.length} for space ${spaceId}`, {
        spaceScoped: relevantEntities.filter((e: any) => e.spaceId === spaceId).length,
        dataspaceLevel: relevantEntities.filter((e: any) => !e.spaceId).length,
        excluded: response.length - relevantEntities.length,
      });

      // Transform entities to proper format
      const entities = relevantEntities.map((entity: any) => {
        // ✅ CRITICAL FIX: Handle both _id and id fields from backend response
        const entityId = entity._id || entity.id || entity.llmId;
        
        const transformedEntity = {
          _id: entityId,
          id: entityId,
          name:
            entity.entityType === 'File'
              ? entity.skeleton?.fileName || entity.name || 'Untitled'
              : (entity.name ?? 'Untitled'),
          type: entity.entityType.toLowerCase(),
          entityType: entity.entityType,
          created_at: entity.created_at || new Date().toISOString(),
          updated_at: entity.updated_at || new Date().toISOString(),
          llmId: entity.llmId, // ✅ CRITICAL FIX: Preserve llmId from backend
          skeleton: {
            '@type': entity.entityType,
            ...entity.skeleton,
          },
          // ✅ CRITICAL: Preserve ACL and user_id for permission checking
          user_id: entity.user_id,
          acl: entity.acl,
          spaceId: entity.spaceId,
        };

        return transformedEntity;
      });

      log.info('[EntityService] Successfully processed entities for space:', {
        spaceId,
        processedCount: entities.length,
        entityTypes: [...new Set(entities.map((e) => e.entityType))],
      });

      return entities;
    } catch (error) {
      log.error('[EntityService] Failed to fetch entities:', error);
      throw error;
    }
  }

  async getAllUserEntities(): Promise<BaseEntityType[]> {
    log.info(`[ENTITY_API_CALL] getAllUserEntities()`);

    try {
      // Get all entities the user has access to (owned + shared via ACL)
      const response = await fetchService(`${API_BASE_URL}/entities`);
      
      if (!Array.isArray(response)) {
        log.error('[EntityService] Expected array response, got:', response);
        throw new Error('Invalid response format from /api/entities');
      }

      log.info(`[EntityService] Received ${response.length} total entities for user`, {
        totalEntities: response.length,
        entityTypes: [...new Set(response.map((e: any) => e.entityType))],
        sharedEntities: response.filter((e: any) => e.acl && e.acl.length > 1).length,
        spaces: [...new Set(response.map((e: any) => e.spaceId).filter(Boolean))],
      });

      // Transform entities to proper format (no space filtering)
      const entities = response.map((entity: any) => {
        const entityId = entity._id || entity.id || entity.llmId;

        const transformedEntity = {
          _id: entityId,
          id: entityId,
          name:
            entity.entityType === 'File'
              ? entity.skeleton?.fileName || entity.name || 'Untitled'
              : (entity.name ?? 'Untitled'),
          type: entity.entityType.toLowerCase(),
          entityType: entity.entityType,
          created_at: entity.created_at || new Date().toISOString(),
          updated_at: entity.updated_at || new Date().toISOString(),
          llmId: entity.llmId,
          skeleton: {
            '@type': entity.entityType,
            ...entity.skeleton,
          },
          // ✅ CRITICAL: Preserve ACL and user_id for permission checking
          user_id: entity.user_id,
          acl: entity.acl,
          spaceId: entity.spaceId,
          trashed: entity.trashed,
          hidden: entity.hidden,
          icon: entity.icon,
          org_id: entity.org_id,
        };

        return transformedEntity;
      });

      log.info('[EntityService] Successfully processed ALL user entities:', {
        processedCount: entities.length,
        entityTypes: [...new Set(entities.map((e) => e.entityType))],
      });

      return entities;
    } catch (error) {
      log.error('[EntityService] Failed to fetch all user entities:', error);
      throw error;
    }
  }

  // ACL Helper Functions
  isEntityOwnedByUser(entity: any, userId?: string): boolean {
    if (!userId) return false;
    return entity.user_id === userId;
  }

  getUserRoleForEntity(entity: any, userId?: string): string | null {
    // CRITICAL FIX: Check ownership FIRST - owner always has maximum permissions
    if (userId && this.isEntityOwnedByUser(entity, userId)) {
      return 'owner';
    }
    
    if (!entity?.acl) {
      return null;
    }
    
    // Then check if user has explicit ACL entry
    if (userId) {
      const userAclEntry = entity.acl.find((entry: any) => entry.subject === userId);
      if (userAclEntry) {
        return userAclEntry.role;
      }
      
      // Check for org ACL entries
      const orgAclEntries = entity.acl.filter((entry: any) => 
        entry.subject && typeof entry.subject === 'string' && entry.subject.startsWith('org:')
      );
      
      if (orgAclEntries.length > 0) {
        // Check if user is a member of any of these orgs
        const { userOrgs } = useDataspaceContextStore.getState();
        
        for (const orgEntry of orgAclEntries) {
          const orgId = orgEntry.subject.replace('org:', '');
          const userOrg = userOrgs.find((org: any) => (org._id || org.id) === orgId);
          
          if (userOrg) {
            // User is a member of this org, return the role from ACL
            return orgEntry.role;
          }
        }
      }
    }
    
    // Finally, fall back to public access
    const publicAclEntry = entity.acl.find((entry: any) => entry.subject === 'public');
    if (publicAclEntry) {
      return publicAclEntry.role;
    }
    
    return null;
  }

  hasExplicitPermission(entity: any, userId?: string): boolean {
    if (!userId) return false;
    
    // CRITICAL FIX: Owner always has explicit permission
    if (this.isEntityOwnedByUser(entity, userId)) {
      return true;
    }
    
    if (!entity?.acl) return false;
    
    // Check if user has explicit ACL entry (not public access)
    const userAclEntry = entity.acl.find((entry: any) => entry.subject === userId);
    if (userAclEntry) {
      return true;
    }
    
    // Check for org ACL entries - organization members have explicit permission
    const orgAclEntries = entity.acl.filter((entry: any) => 
      entry.subject && typeof entry.subject === 'string' && entry.subject.startsWith('org:')
    );
    
    if (orgAclEntries.length > 0) {
      // Check if user is a member of any of these orgs
      const { userOrgs } = useDataspaceContextStore.getState();
      
      for (const orgEntry of orgAclEntries) {
        const orgId = orgEntry.subject.replace('org:', '');
        const userOrg = userOrgs.find((org: any) => (org._id || org.id) === orgId);
        
        if (userOrg) {
          // User is a member of this org, they have explicit permission
          return true;
        }
      }
    }
    
    return false;
  }

  hasEntityPermission(entity: any, userId?: string, action: string = 'read'): boolean {
    // Check if user owns the entity (for userId provided)
    if (userId && this.isEntityOwnedByUser(entity, userId)) {
      return true;
    }
    
    // Check ACL permissions (includes public access)
    const role = this.getUserRoleForEntity(entity, userId);
    if (!role) {
      // If no userId provided, still check for public access
      if (!userId && entity.acl) {
        const publicEntry = entity.acl.find((entry: any) => entry.subject === 'public');
        if (publicEntry) {
          const publicRole = publicEntry.role;
          const permissions: Record<string, string[]> = {
            'owner': ['read', 'write', 'delete', 'share', 'manage_acl', 'comment'],
            'editor': ['read', 'write', 'share', 'comment'],
            'viewer': ['read'],
            'commenter': ['read', 'comment']
          };
          return permissions[publicRole]?.includes(action) || false;
        }
      }
      return false;
    }
    
    const permissions: Record<string, string[]> = {
      'owner': ['read', 'write', 'delete', 'share', 'manage_acl', 'comment'],
      'editor': ['read', 'write', 'share', 'comment'],
      'viewer': ['read'],
      'commenter': ['read', 'comment']
    };
    
    return permissions[role]?.includes(action) || false;
  }

  async getEntitiesByType(params: { 
    entityType: string;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<BaseEntityType[]> {
    const { entityType, limit, sortBy, sortOrder } = params;
    
    log.info(`[ENTITY_API_CALL] getEntitiesByType() | Type: ${entityType}`);

    try {
      const queryParams = new URLSearchParams({
        type: entityType,
      });

      if (limit) {
        queryParams.set('limit', limit.toString());
      }

      if (sortBy) {
        queryParams.set('sortBy', sortBy);
      }

      if (sortOrder) {
        queryParams.set('sortOrder', sortOrder);
      }

      const response = await fetchService(`${this.baseUrl}/entities?${queryParams.toString()}`, {
        credentials: 'include',
      });

      if (!Array.isArray(response)) {
        log.error('[EntityService] Expected array response, got:', response);
        throw new Error('Invalid response format from /api/entities');
      }

      const entities = response.map((entity: any) => {
        const entityId = entity._id || entity.id || entity.llmId;
        
        return {
          _id: entityId,
          id: entityId,
          name:
            entity.entityType === 'File'
              ? entity.skeleton?.fileName || entity.name || 'Untitled'
              : (entity.name ?? 'Untitled'),
          type: entity.entityType.toLowerCase(),
          entityType: entity.entityType,
          created_at: entity.created_at || new Date().toISOString(),
          updated_at: entity.updated_at || new Date().toISOString(),
          llmId: entity.llmId,
          skeleton: entity.skeleton || {},
          appId: entity.appId,
          user_id: entity.user_id,
          acl: entity.acl,
          spaceId: entity.spaceId,
          trashed: entity.trashed,
          hidden: entity.hidden,
          icon: entity.icon,
          org_id: entity.org_id,
        };
      });

      // Register mappings for all entities that have llmId
      const { registerEntityMapping } = await import('../utils/llmIdMapper');
      entities.forEach(registerEntityMapping);

      return entities;
    } catch (error) {
      log.error(`[EntityService] Failed to fetch ${entityType} entities:`, error);
      throw error;
    }
  }
  
  private _triggerSpaceWindowCleanup(entityId: string, originalId: string): void {
    /**
     * Trigger real-time cleanup of space windows that reference the deleted entity.
     * This ensures the frontend updates immediately without waiting for a full refresh.
     */
    try {
      // Import spaceStore dynamically to avoid circular dependencies
      import('../stores/spaceStore').then(async ({ useSpaceStore }) => {
        const spaceStore = useSpaceStore.getState();
        
        log.info(`[EntityService] Triggering space window cleanup for deleted entity ${entityId}`);
        
        // Find windows that reference the deleted entity
        const windowsToRemove: string[] = [];
        const contextToRemove: string[] = [];
        
        // Check each window for references to the deleted entity (now async)
        const windowChecks = Object.entries(spaceStore.windows).map(async ([windowId, window]) => {
          const shouldRemove = await this._windowReferencesEntity(window, entityId, originalId);
          if (shouldRemove) {
            windowsToRemove.push(windowId);
            log.info(`[EntityService] Window ${windowId} references deleted entity - marking for removal`);
          }
        });
        
        await Promise.all(windowChecks);
        
        // Check context entities
        Object.keys(spaceStore.contextEntities || {}).forEach(contextEntityId => {
          if (contextEntityId === originalId || contextEntityId === entityId) {
            contextToRemove.push(contextEntityId);
            log.info(`[EntityService] Context entity ${contextEntityId} references deleted entity - marking for removal`);
          }
        });
        
        // Remove the windows and context entities
        if (windowsToRemove.length > 0) {
          log.info(`[EntityService] Removing ${windowsToRemove.length} orphaned windows: ${windowsToRemove.join(', ')}`);
          windowsToRemove.forEach(windowId => {
            spaceStore.removeWindow(windowId);
          });
        }
        
        if (contextToRemove.length > 0) {
          log.info(`[EntityService] Removing ${contextToRemove.length} orphaned context entities: ${contextToRemove.join(', ')}`);
          // Remove from context entities (this would need to be implemented in spaceStore)
          contextToRemove.forEach(contextEntityId => {
            spaceStore.removeContextEntity?.(contextEntityId);
          });
        }
        
        if (windowsToRemove.length === 0 && contextToRemove.length === 0) {
          log.info(`[EntityService] No orphaned windows or context entities found for deleted entity ${entityId}`);
        }
        
      }).catch(error => {
        log.error(`[EntityService] Error during space window cleanup:`, error);
      });
      
    } catch (error) {
      log.error(`[EntityService] Error triggering space window cleanup:`, error);
      // Don't throw - this is a best-effort cleanup
    }
  }
  
  private async _windowReferencesEntity(window: any, entityId: string, originalId: string): Promise<boolean> {
    /**
     * Check if a window references the deleted entity in any way
     */
    // Import helper
    const { getWindowEntityId } = await import('../components/Space/utils/windowUtils');
    
    // Check root-level references (new standard)
    if (window.entityId === entityId || window.entityId === originalId ||
        window.entityLlmId === entityId || window.entityLlmId === originalId) {
      return true;
    }
    
    // Check via centralized helper (handles legacy applicationState)
    const windowEntityId = getWindowEntityId(window);
    if (windowEntityId === entityId || windowEntityId === originalId) {
      return true;
    }
    
    return false;
  }

  async addLink(fromId: string, toId: string, relationType: string, properties?: Record<string, any>): Promise<void> {
    log.info(`[EntityService] Adding link: ${fromId} --[${relationType}]--> ${toId}`);
    
    try {
      // Use entity_edges collection instead of embedded links field
      const response = await fetchService(`${this.baseUrl}/entity-edges`, {
        method: 'POST',
        body: JSON.stringify({
          from_id: fromId,
          to_id: toId,
          rel: relationType,
          properties: properties || {}
        })
      });
      
      log.info(`[EntityService] ✅ Link added via entity_edges collection`);
    } catch (error) {
      log.error(`[EntityService] Failed to add link:`, error);
      throw error;
    }
  }

  async removeLink(fromId: string, toId: string, relationType: string): Promise<void> {
    log.info(`[EntityService] Removing link: ${fromId} --[${relationType}]--> ${toId}`);
    
    try {
      // Use entity_edges collection
      await fetchService(`${this.baseUrl}/entity-edges`, {
        method: 'DELETE',
        body: JSON.stringify({
          from_id: fromId,
          to_id: toId,
          rel: relationType
        })
      });
      
      log.info(`[EntityService] ✅ Link removed via entity_edges collection`);
    } catch (error: any) {
      // If edge doesn't exist (404), that's okay - it's already not linked
      if (error.message?.includes('404') || error.message?.includes('Edge not found')) {
        log.info(`[EntityService] ⏭️ Edge doesn't exist (already unlinked or legacy data)`);
        return;
      }
      
      log.error(`[EntityService] Failed to remove link:`, error);
      throw error;
    }
  }

  async getEntityEdges(entityId: string, direction: 'outgoing' | 'incoming' | 'both' = 'both', relationType?: string): Promise<any[]> {
    log.info(`[EntityService] Getting edges for: ${entityId} (${direction})`);
    
    try {
      const params = new URLSearchParams({
        direction
      });
      
      if (relationType) {
        params.append('rel', relationType);
      }
      
      const edges = await fetchService(`${this.baseUrl}/entities/${entityId}/edges?${params.toString()}`, {
        method: 'GET'
      });
      
      log.info(`[EntityService] ✅ Found ${edges.length} edges`);
      return edges;
    } catch (error) {
      log.error(`[EntityService] Failed to get edges:`, error);
      throw error;
    }
  }

  async queryEdges(params: {
    from_id?: string;
    to_id?: string;
    rel?: string;
    from_type?: string;
    to_type?: string;
    limit?: number;
  }): Promise<any[]> {
    log.info(`[EntityService] Querying edges:`, params);
    
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
      
      const edges = await fetchService(`${this.baseUrl}/entity-edges?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      log.info(`[EntityService] ✅ Found ${edges.length} edges`);
      return edges;
    } catch (error) {
      log.error(`[EntityService] Failed to query edges:`, error);
      throw error;
    }
  }

  async _legacyRemoveLink(fromId: string, toId: string, relationType?: string): Promise<void> {
    log.info(`[EntityService] LEGACY: Removing embedded link: ${fromId} --[${relationType || 'all'}]--> ${toId}`);
    
    try {
      const fromEntity = await this.get(fromId);
      const links = (fromEntity as any).links || {};
      
      if (relationType) {
        const relationLinks = links[relationType] || [];
        links[relationType] = relationLinks.filter((id: string) => id !== toId);
        if (links[relationType].length === 0) {
          delete links[relationType];
        }
      } else {
        Object.keys(links).forEach(rel => {
          links[rel] = links[rel].filter((id: string) => id !== toId);
          if (links[rel].length === 0) {
            delete links[rel];
          }
        });
      }
      
      await this.update(fromId, { links } as any);
      
      log.info(`[EntityService] ✅ Link removed successfully`);
    } catch (error) {
      log.error(`[EntityService] Failed to remove link:`, error);
      throw error;
    }
  }

  async findLinked(fromId: string, relationType?: string, entityType?: string): Promise<T[]> {
    log.info(`[EntityService] Finding linked entities from ${fromId}`, { relationType, entityType });
    
    try {
      const fromEntity = await this.get(fromId);
      const links = (fromEntity as any).links || {};
      
      let linkedIds: string[] = [];
      if (relationType) {
        linkedIds = links[relationType] || [];
      } else {
        linkedIds = (Object.values(links) as string[][]).flat();
      }
      
      if (linkedIds.length === 0) {
        return [];
      }
      
      const linkedEntities = await Promise.all(
        linkedIds.map(id => this.get(id).catch(() => null))
      );
      
      let results = linkedEntities.filter(e => e !== null) as T[];
      
      if (entityType) {
        results = results.filter(e => e.entityType === entityType);
      }
      
      log.info(`[EntityService] ✅ Found ${results.length} linked entities`);
      return results;
    } catch (error) {
      log.error(`[EntityService] Failed to find linked entities:`, error);
      throw error;
    }
  }
}

// Export a singleton instance with smart environment detection
const getSmartApiUrl = () => {
  const hostname = window.location.hostname;
  
  // Smart detection: ignore .env in production, use hostname-based detection
  const result = hostname === 'app.riversystem.ai' || hostname === 'riversystem.ai' || hostname.endsWith('.riversystem.ai')
    ? 'https://app.riversystem.ai/api'   // Selfhosted
    : hostname === 'rivereditor.com' 
    ? 'https://rivereditor.com/api'      // Production - Temporary: Using CloudFront until wildcard SSL cert ready
    : 'http://localhost:5001/api';       // Local development
    
  return result;
};

// Always use smart detection - ignore environment variables
export const entityService = new EntityService({
  baseUrl: getSmartApiUrl(),
});

// Export the getEntitiesByType function directly
export const getEntitiesByType = (params: {
  entityType: string;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}) => {
  return entityService.getEntitiesByType(params);
};

// Usage example:
// export const createEntityService = <T extends BaseEntityType>(entityType: EntityTypes) => {
//   return new EntityService<T>({
//     baseUrl: `
